import React, { ChangeEvent, useContext, useState } from "react";
import ExcelJS from "exceljs";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import IPrice_Group from "../../../../models/IPrice_Group";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import RestEndpointsEnum from "../../../../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch, buildRestUrl } from "../../../../services/RestService";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import { NYSLA_DEFAULTS } from "../../../../models/NYSLAConstants";
import { INYSLASkippedItemDTO } from "../../../../models/DTO/INYSLAExportDTO";
import "./NYSLAExportModal.scss";

type Props = {
    closeModalFn: () => void;
    catalogGuid: string;
    priceGroups: IPrice_Group[];
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
};

// Month options for dropdown
const MONTH_OPTIONS = [
    { value: 1, title: "January" },
    { value: 2, title: "February" },
    { value: 3, title: "March" },
    { value: 4, title: "April" },
    { value: 5, title: "May" },
    { value: 6, title: "June" },
    { value: 7, title: "July" },
    { value: 8, title: "August" },
    { value: 9, title: "September" },
    { value: 10, title: "October" },
    { value: 11, title: "November" },
    { value: 12, title: "December" },
];

// Post type options
const POST_TYPE_OPTIONS = [{ value: "WR", title: "WR - Wholesale/Retail" }];

interface ExportResult {
    exportedCount: number;
    skippedCount: number;
    skippedItems: INYSLASkippedItemDTO[];
    warnings: string[];
}

export const NYSLAExportModal: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const currentDate = new Date();
    const [priceGroupGuid, setPriceGroupGuid] = useState<string>("");
    const [postMonth, setPostMonth] = useState<number>(currentDate.getMonth() + 1);
    const [postYear, setPostYear] = useState<number>(currentDate.getFullYear());
    const [postType, setPostType] = useState<string>(NYSLA_DEFAULTS.POST_TYPE);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportResult, setExportResult] = useState<ExportResult | null>(null);
    const [showSkippedItems, setShowSkippedItems] = useState<boolean>(false);

    const closeModal = () => {
        props.closeModalFn();
    };

    const handlePriceGroupChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPriceGroupGuid(e.target.value);
    };

    const handleMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPostMonth(Number(e.target.value));
    };

    const handleYearChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPostYear(Number(e.target.value));
    };

    const handlePostTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPostType(e.target.value);
    };

    const handleExport = async () => {
        if (!priceGroupGuid) {
            const notification: INotification = {
                message: "please_select_price_group",
                level: NotificationLevelEnum.warning,
                position: NotificationPositionEnum.bc,
            };
            props.notifyFn(null, notification);
            return;
        }

        setIsExporting(true);
        setExportResult(null);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.WineNYSLAExport,
                params: { catalogGuid: props.catalogGuid },
            });

            const body = {
                priceGroupGuid,
                postMonth,
                postYear,
                postType,
            };

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            // Extract metadata from response headers
            const exportedCount = parseInt(response.headers.get("X-NYSLA-Exported-Count") || "0", 10);
            const skippedCount = parseInt(response.headers.get("X-NYSLA-Skipped-Count") || "0", 10);
            const skippedItemsHeader = response.headers.get("X-NYSLA-Skipped-Items");
            const warningsHeader = response.headers.get("X-NYSLA-Warnings");

            let skippedItems: INYSLASkippedItemDTO[] = [];
            let warnings: string[] = [];

            try {
                if (skippedItemsHeader) {
                    skippedItems = JSON.parse(atob(skippedItemsHeader));
                }
                if (warningsHeader) {
                    warnings = JSON.parse(atob(warningsHeader));
                }
            } catch (parseError) {
                console.error("Error parsing response headers:", parseError);
            }

            setExportResult({ exportedCount, skippedCount, skippedItems, warnings });

            // Download the CSV
            const blob = await response.blob();
            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = `nysla-export-${postYear}-${String(postMonth).padStart(2, "0")}.csv`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, "");
                }
            }

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);

            const notification: INotification = {
                message: "nysla_export_complete",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };
            props.notifyFn(null, notification);
        } catch (error) {
            console.error("Error downloading NYSLA export:", error);

            const notification: INotification = {
                message: error instanceof Error ? error.message : "nysla_export_failed",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: error instanceof Error,
            };
            props.notifyFn(null, notification);
        } finally {
            setIsExporting(false);
        }
    };

    const canExport = priceGroupGuid && postMonth && postYear && !isExporting;

    const downloadIssuesReport = async () => {
        if (!exportResult) return;

        const workbook = new ExcelJS.Workbook();

        // Skipped Items Tab
        const skippedWs = workbook.addWorksheet("Skipped Items");
        skippedWs.columns = [
            { header: "Item Name", key: "itemName", width: 40 },
            { header: "Reason", key: "reason", width: 60 },
        ];
        skippedWs.getRow(1).font = { bold: true };
        exportResult.skippedItems.forEach((item) => {
            skippedWs.addRow({ itemName: item.itemName, reason: item.reason });
        });

        // Warnings Tab
        const warningsWs = workbook.addWorksheet("Warnings");
        warningsWs.columns = [{ header: "Warning", key: "warning", width: 80 }];
        warningsWs.getRow(1).font = { bold: true };
        exportResult.warnings.forEach((warning) => {
            warningsWs.addRow({ warning });
        });

        // Download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `nysla-export-issues-${postYear}-${String(postMonth).padStart(2, "0")}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
    };

    const hasIssues = exportResult && (exportResult.skippedItems.length > 0 || exportResult.warnings.length > 0);

    return (
        <div className={"NYSLAExportModal p-3 w-100"}>
            <div className={"w-100"}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <OwTitle leftIconName={"fad fa-file-certificate"} title={"nysla_price_posting_export"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div className={"mt-3"}>
                <div className={"mb-3"}>
                    <OwLabel title={"select_price_group"} />
                    <OwSelectOption
                        fn={handlePriceGroupChange}
                        name={"priceGroupGuid"}
                        options={props.priceGroups}
                        valueProp={"guid"}
                        titleProp={"value"}
                        showBlankFirst={true}
                        selectedOption={priceGroupGuid}
                        hasSelectedOptionProp={false}
                    />
                </div>

                <div className={"mb-3"}>
                    <OwLabel title={"nysla_post_month"} />
                    <OwSelectOption
                        fn={handleMonthChange}
                        name={"postMonth"}
                        options={MONTH_OPTIONS}
                        valueProp={"value"}
                        titleProp={"title"}
                        selectedOption={postMonth}
                        hasSelectedOptionProp={false}
                    />
                </div>

                <div className={"mb-3"}>
                    <OwLabel title={"nysla_post_year"} />
                    <OwInputWrapper fn={handleYearChange} name={"postYear"} value={postYear} type={"number"} min={2000} max={2100} />
                </div>

                <div className={"mb-3"}>
                    <OwLabel title={"nysla_post_type"} />
                    <OwSelectOption
                        fn={handlePostTypeChange}
                        name={"postType"}
                        options={POST_TYPE_OPTIONS}
                        valueProp={"value"}
                        titleProp={"title"}
                        selectedOption={postType}
                    />
                </div>

                <div className={"mb-3"}>
                    <OwButton
                        fn={handleExport}
                        title={"export"}
                        className={"btn-primary"}
                        active={isExporting}
                        disableOnActive={true}
                        disabled={!canExport}
                        leftIconClass={isExporting ? "fas fa-spinner fa-spin" : "fas fa-download"}
                    />
                </div>

                {exportResult && (
                    <div className={"export-results mt-3 p-3 border rounded"}>
                        <OwTitle title={"export_results"} tag={"h5"} />

                        <div className={"d-flex gap-4 mt-2 flex-wrap align-items-center"}>
                            <div>
                                <span className={"fw-bold text-success"}>{exportResult.exportedCount}</span>
                                <span className={"ms-1"}>items exported</span>
                            </div>
                            {exportResult.skippedItems.length > 0 && (
                                <div>
                                    <span className={"fw-bold text-warning"}>{exportResult.skippedItems.length}</span>
                                    <span className={"ms-1"}>items skipped</span>
                                </div>
                            )}
                            {exportResult.warnings.length > 0 && (
                                <div>
                                    <span className={"fw-bold text-warning"}>{exportResult.warnings.length}</span>
                                    <span className={"ms-1"}>warnings</span>
                                </div>
                            )}
                            {hasIssues && (
                                <OwButton
                                    fn={downloadIssuesReport}
                                    title={"download_issues_report"}
                                    className={"btn-sm btn-outline-warning"}
                                    leftIconClass={"fas fa-file-excel"}
                                />
                            )}
                        </div>

                        {exportResult.skippedItems.length > 0 && (
                            <div className={"mt-2"}>
                                <OwButton
                                    fn={() => setShowSkippedItems(!showSkippedItems)}
                                    title={showSkippedItems ? "hide_skipped_items" : "show_skipped_items"}
                                    className={"btn-sm btn-outline-secondary"}
                                />
                                {showSkippedItems && (
                                    <div className={"mt-2 skipped-items-list"}>
                                        {exportResult.skippedItems.map((item, index) => (
                                            <div key={index} className={"skipped-item p-2 border-bottom"}>
                                                <div className={"fw-bold"}>{item.itemName}</div>
                                                <div className={"text-muted small"}>{item.reason}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {exportResult.warnings.length > 0 && (
                            <div className={"mt-2"}>
                                <OwLabel title={"warnings"} className={"text-warning"} leftIconClass={"fas fa-exclamation-triangle"} />
                                <ul className={"mb-0 ps-3"}>
                                    {exportResult.warnings.map((warning, index) => (
                                        <li key={index} className={"text-warning small"}>
                                            {warning}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
